import { getUniqueId, removeNulls, safeParseNullDefault } from 'shouldve-been-js';
import Company from '../../Models/CompanyModel';
import SystemInfo from '../../Models/SystemInfoModel';
import Token from '../../Models/TokenModel';
import VesselType from '../../Models/VesselTypeModel';
import Vessel from '../../Models/VesselModel';
import BugReport from '../../Models/BugReportModel';
import Tenant from '../../Models/TenantModel';

class ApiService {

    private _baseUrl: string;
    private _addBugReport: (newBugReport: BugReport) => void;

    public constructor(baseUrl: string, addBugReport: (newBugReport: BugReport) => void) {
        this._baseUrl = baseUrl;
        this._addBugReport = addBugReport;
    }

    public get baseUrl(): string {
        return this._baseUrl;
    }

    public set baseUrl(value: string) {
        this._baseUrl = value;
    }

    public get addBugReport(): (newBugReport: BugReport) => void {
        return this._addBugReport;
    }

    public set addBugReport(value: (newBugReport: BugReport) => void) {
        this._addBugReport = value;
    }

    public copy(): ApiService {
        const newApi: ApiService = new ApiService(this.baseUrl, this.addBugReport);
        return newApi;
    }

    public static async getTenantsByEmail(email: string): Promise<Tenant[]> {

        try {
            const response: Response = await fetch(
                'https://api.prod.k-fleet.cloud/tenants/user-tenants/' + email,
                { 
                    mode: 'cors',
                    method: 'GET',
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse)) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const tenants: (Tenant | null)[] = jsonResponse?.map((jsonObject: any) => {
                return Tenant.fromJson(jsonObject);
            });

            return removeNulls(tenants);  

        } catch (e: unknown) {
            return [];
        }
    }

    public async fetchNewToken(email: string, password: string, tenantId: string): Promise<Token | null> {

        const tokenUrl: string = '/v1/token';

        try {
            const response: Response = await fetch(
                this.baseUrl + tokenUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password,
                        tenantId: tenantId,
                    }),
                },
            );

            if (response.status >= 400) { //if failed to fetch token
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to fetch new token - Statuscode error', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const tokenInfo: Token | null = Token.fromJson(jsonResponse);

            return tokenInfo;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to fetch new token - Unknown error', e?.message ?? ''));
            return null;
        }
    }

    public async getHealth(): Promise<boolean> {

        const endpointUrl: string = '/v1/system/health';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Health endpoint failed - Status code error', response.status.toString() + ' ' + (await response.text())));
                return false;
            }

            return true;       
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Health endpoint failed - Unknown error', e?.message ?? ''));
            return false;  
        }
    }

    public async getSystemInfo(): Promise<SystemInfo | null> {

        const endpointUrl: string = '/v1/system';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get system info - Status code error', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            return SystemInfo.fromJson(jsonResponse);
            
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get system info - Unknown error', e?.message ?? ''));
            return null;  
        }
    }

    public async getCompanies(token: string | null): Promise<Company[]> {

        if (token === null) {
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get companies - Token is null', ''));
            return [];
        }

        const endpointUrl: string = '/v1/companies';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to fetch companies - Status code error', response.status.toString() + ' ' + (await response.text())));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse)) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get system info - Response is not an array', ''));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const companies: (Company | null)[] = jsonResponse?.map((jsonObject: any) => {
                return Company.fromJson(jsonObject);
            });

            return removeNulls(companies);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get companies - Unknown error', e?.message ?? ''));
            return [];  
        }
    }

    public async getCustomerName(token: string | null): Promise<string | null> {

        if (token === null) {
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get customer name - Token is null', ''));
            return null;
        }

        const endpointUrl: string = '/v1/customers/me';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get customer name - Status code error', response.status.toString() + ' ' + (await response.text())));
                return null;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            const customerName: string | null = safeParseNullDefault<string>(jsonResponse?.name, '');

            return customerName;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get customer name - Unknown error', e?.message ?? ''));
            return null;  
        }
    }

    public async getVessels(token: string | null): Promise<Vessel[]> {

        if (token === null) {
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessels - Token is null', ''));
            return [];
        }

        const endpointUrl: string = '/v1/vessels';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessels - Status code error', response.status.toString() + ' ' + (await response.text())));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse)) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessels - Response is not an array', ''));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const vessels: (Vessel | null)[] = jsonResponse?.map((jsonObject: any) => {
                return Vessel.fromJson(jsonObject);
            });

            return removeNulls(vessels);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessels - Unknown error', e?.message ?? ''));
            return [];  
        }
    }

    public async saveNewVessel(sortNo: number, vesselCode: string, vesselName: string, companyNo: number, vesselTypeId: string | null, shipNew: string, imoNumber: string | null, currCode: string | null, inPurchase: boolean, inMaintenance: boolean, inBudget: boolean, inExperience: boolean, inVoyage: boolean, inLogbook: boolean, inDocking: boolean, token: string | null): Promise<string | null> { //either returns a string with an error message, or null if succeeds

        if (token === null) {
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to save new vessel - Token is null', ''));
            return 'Token is missing';
        }

        const endpointUrl: string = '/v1/vessels';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sortNo: sortNo,
                        vesselCode: vesselCode,
                        vesselName: vesselName,
                        companyNo: companyNo,
                        vesselTypeId: vesselTypeId,
                        shipNew: shipNew,
                        imoNumber: imoNumber,
                        currCode: currCode,
                        inPurchase: inPurchase,
                        inMaintenance: inMaintenance,
                        inBudget: inBudget,
                        inExperience: inExperience,
                        inVoyage: inVoyage,
                        inLogbook: inLogbook,
                        inDocking: inDocking,
                    }),
                },
            ); 
            
            if (response.status === 400) { //find expected errors (only check those that are not checked frontend)
                const responseMessage: string = await response.text();

                if (responseMessage === 'vesselCode must not match any existing systemCode or vesselCode') {
                    return 'Vessel Code must be unique';
                } else if (responseMessage === 'companyNo must be a number' || responseMessage === 'companyNo must match the companyNo of one of the customer\'s companies') {
                    return 'Company is not valid';  
                } else if (responseMessage === 'vesselTypeId must be either a string or null' || responseMessage === 'vesselTypeId must match a valid vesselType') {
                    return 'Vessel Type is not valid';  
                } else if (responseMessage === 'shipNew must be a string of length 4' || responseMessage === 'The first two characters of shipNew must be digits representing a valid month (between 01 and 12)' || responseMessage === 'The last two characters of shipNew must be digits representing a valid year (between 00 and 99)') {
                    return 'Built Date is not valid';  
                } else if (responseMessage === 'currCode must be a string or null' || responseMessage === 'currCode must be null as all other vessels have value null' || responseMessage === 'currCode must be a string matching the currCode of an existing vessel') {
                    return 'Currency is not valid';  
                } else {
                    return 'Something went wrong on the server side';
                }

            } else if (response.status > 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to save new vessel - Status code error', response.status.toString() + ' ' + (await response.text())));
                return 'Something went wrong on the server side';
            }

            return null; //success

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to save new vessel - Unknown error', e?.message ?? ''));
            return 'Something went wrong on the server side';  
        }
    }

    public async getVesselTypes(token: string | null): Promise<VesselType[]> {

        if (token === null) {
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessel types - Token is null', ''));
            return [];
        }

        const endpointUrl: string = '/v1/vessels/types';

        try {
            const response: Response = await fetch(
                this.baseUrl + endpointUrl, 
                { 
                    mode: 'cors',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                },
            );

            if (response.status >= 400) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessel types - Status code error', response.status.toString() + ' ' + (await response.text())));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            const jsonResponse: any = await response.json();

            if (!Array.isArray(jsonResponse)) {
                this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessel types - Response is not an array', ''));
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
            const vesselTypes: (VesselType | null)[] = jsonResponse?.map((jsonObject: any) => {
                return VesselType.fromJson(jsonObject);
            });

            return removeNulls(vesselTypes);  

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.addBugReport(new BugReport(getUniqueId(), new Date(), 'Failed to get vessel types - Unknown error', e?.message ?? ''));
            return [];  
        }
    }
}

export default ApiService;